<script setup lang="ts">
import MainLayout from '@/common/components/MainLayout.vue';
import { Tabs, Tab, TabList, TabPanels, TabPanel } from 'primevue';
import { useTranslations } from 'shared/translations/composables/useTranslations';
import InboundDeliveryList from './components/InboundDeliveryList.vue';
import OutboundDeliveryList from './components/OutboundDeliveryList.vue';
import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

const translations = useTranslations();

type DeliveryTab = 'inbound-deliveries' | 'outbound-deliveries';

function normalizeTab(tab: unknown): DeliveryTab {
  return tab === 'outbound-deliveries' ? 'outbound-deliveries' : 'inbound-deliveries';
}

const route = useRoute();
const router = useRouter();
const selectedTab = ref<DeliveryTab>(normalizeTab(route.query.tab));

watch(
  () => route.query.tab,
  (queryTab) => {
    const normalized = normalizeTab(queryTab);
    if (selectedTab.value !== normalized) {
      selectedTab.value = normalized;
    }
  },
);

watch(selectedTab, async (tab) => {
  const current = normalizeTab(route.query.tab);
  if (current === tab) return;

  await router.replace({
    name: 'delivery',
    query: {
      ...route.query,
      tab,
    },
  });
});
</script>

<template>
  <MainLayout>
    <Tabs class="delivery-tabs" v-model:value="selectedTab">
      <TabList class="delivery-tab-list">
        <Tab value="inbound-deliveries">{{ translations.inboundDelivery.listPage.title }}</Tab>
        <Tab value="outbound-deliveries">{{ translations.outboundDelivery.listPage.title }}</Tab>
      </TabList>
      <TabPanels class="delivery-panels">
        <TabPanel class="delivery-panel" value="inbound-deliveries">
          <InboundDeliveryList></InboundDeliveryList>
        </TabPanel>
        <TabPanel class="delivery-panel" value="outbound-deliveries">
          <OutboundDeliveryList></OutboundDeliveryList>
        </TabPanel>
      </TabPanels>
    </Tabs>
  </MainLayout>
</template>

<style scoped>
.delivery-tabs {
  display: grid;
  grid-template-rows: 36px 1fr;
}

.delivery-tabs,
.delivery-panels {
  overflow-y: hidden;
  height: 100%;
}

:deep(.delivery-tab-list.p-tablist) {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  overflow: hidden;
}

:deep(.delivery-panels.p-tabpanels) {
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}

:deep(.delivery-panel) {
  overflow-y: hidden;
  height: 100%;
}
</style>
